import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDateTime } from '../utils/dateFormat';
import './BossMenu.css';

function BossMenu() {
  const { apiFetch, hasPermission } = useAuth();
  const { language } = useI18n();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [accountData, setAccountData] = useState([]);
  const [scheduledDeposits, setScheduledDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [operationLoading, setOperationLoading] = useState(false);
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [activeTab, setActiveTab] = useState('instant');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleFormData, setScheduleFormData] = useState({
    amount: '',
    interval_type: 'daily',
    interval_days: 1,
    monthly_day: 1,
    execution_hour: 0,
    execution_minute: 0,
    notes: ''
  });

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch('/api/companies/accounts');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch companies');
      setCompanies(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const fetchAccountData = useCallback(async (accountName) => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch(`/api/companies/account-data?account=${encodeURIComponent(accountName)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch account data');
      setAccountData(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const fetchScheduledDeposits = useCallback(async () => {
    try {
      const res = await apiFetch('/api/companies/scheduled-deposits');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch scheduled deposits');
      setScheduledDeposits(data.data || []);
    } catch (err) {
      console.error('Error fetching scheduled deposits:', err.message);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchCompanies();
    fetchScheduledDeposits();
  }, [fetchCompanies, fetchScheduledDeposits]);

  const canView = typeof hasPermission === 'function' ? hasPermission('companies.view') : true;
  const canEdit = typeof hasPermission === 'function' ? hasPermission('companies.edit') : false;
  const canSchedule = typeof hasPermission === 'function' ? hasPermission('companies.schedule.edit') : false;

  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const label = (company.label || '').toLowerCase();
      const name = (company.name || '').toLowerCase();
      const query = companySearch.toLowerCase();
      return label.includes(query) || name.includes(query);
    });
  }, [companies, companySearch]);

  const selectedSchedules = useMemo(() => {
    if (!selectedCompany) return [];
    return scheduledDeposits.filter((schedule) => schedule.company_name === selectedCompany.name);
  }, [scheduledDeposits, selectedCompany]);

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setAccountData([]);
    setDepositAmount('');
    setWithdrawAmount('');
    setShowDepositForm(false);
    setShowWithdrawForm(false);
    setShowScheduleForm(false);
    setActiveTab('instant');
    fetchAccountData(company.name);
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      setError(language === 'da' ? 'Venligst angiv et gyldigt beløb' : 'Please enter a valid amount');
      return;
    }

    try {
      setOperationLoading(true);
      setError(null);
      const res = await apiFetch('/api/companies/deposit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          society: selectedCompany.name,
          amount: parseInt(depositAmount, 10)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Deposit failed');

      setSuccess(language === 'da' ? `${depositAmount} indsat i ${selectedCompany.label}` : `${depositAmount} deposited to ${selectedCompany.label}`);
      setDepositAmount('');
      setShowDepositForm(false);
      await fetchAccountData(selectedCompany.name);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setError(language === 'da' ? 'Venligst angiv et gyldigt beløb' : 'Please enter a valid amount');
      return;
    }

    try {
      setOperationLoading(true);
      setError(null);
      const res = await apiFetch('/api/companies/withdraw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          society: selectedCompany.name,
          amount: parseInt(withdrawAmount, 10)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Withdrawal failed');

      setSuccess(language === 'da' ? `${withdrawAmount} trukket fra ${selectedCompany.label}` : `${withdrawAmount} withdrawn from ${selectedCompany.label}`);
      setWithdrawAmount('');
      setShowWithdrawForm(false);
      await fetchAccountData(selectedCompany.name);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    if (!scheduleFormData.amount || parseFloat(scheduleFormData.amount) <= 0) {
      setError(language === 'da' ? 'Venligst angiv et gyldigt beløb' : 'Please enter a valid amount');
      return;
    }

    try {
      setOperationLoading(true);
      setError(null);
      const intervalDays = scheduleFormData.interval_type === 'monthly'
        ? scheduleFormData.monthly_day
        : scheduleFormData.interval_days;

      const res = await apiFetch('/api/companies/scheduled-deposits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: selectedCompany.name,
          company_label: selectedCompany.label,
          amount: parseInt(scheduleFormData.amount, 10),
          interval_type: scheduleFormData.interval_type,
          interval_days: parseInt(intervalDays, 10),
          execution_hour: parseInt(scheduleFormData.execution_hour, 10),
          execution_minute: parseInt(scheduleFormData.execution_minute, 10),
          notes: scheduleFormData.notes
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create scheduled deposit');

      setSuccess(language === 'da' ? 'Indsætning planlagt' : 'Deposit scheduled');
      setScheduleFormData({
        amount: '',
        interval_type: 'daily',
        interval_days: 1,
        monthly_day: 1,
        execution_hour: 0,
        execution_minute: 0,
        notes: ''
      });
      setShowScheduleForm(false);
      fetchScheduledDeposits();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm(language === 'da' ? 'Vil du slette denne planlagte indsætning?' : 'Delete this scheduled deposit?')) {
      return;
    }

    try {
      setError(null);
      const res = await apiFetch(`/api/companies/scheduled-deposits/${scheduleId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete scheduled deposit');

      setSuccess(language === 'da' ? 'Planlagt indsætning slettet' : 'Scheduled deposit deleted');
      fetchScheduledDeposits();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat(language === 'da' ? 'da-DK' : 'en-US').format(amount || 0);

  const getTotalBalance = () => {
    return accountData.reduce((sum, row) => sum + (row.money || 0), 0);
  };

  const selectedCompanyStats = {
    accounts: accountData.length,
    schedules: selectedSchedules.length,
    balance: getTotalBalance()
  };

  const formatDate = (dateString) => {
    return formatEuDateTime(dateString, '-');
  };

  const getDaySuffix = (day) => {
    if (day === 1 || day === 21 || day === 31) return 'st';
    if (day === 2 || day === 22) return 'nd';
    if (day === 3 || day === 23) return 'rd';
    return 'th';
  };

  const getIntervalLabel = (type, value) => {
    if (type === 'daily') return language === 'da' ? 'Daglig' : 'Daily';
    if (type === 'weekly') return language === 'da' ? 'Ugentlig' : 'Weekly';
    if (type === 'monthly') {
      const day = value || 1;
      return language === 'da' ? `${day}. dag hver måned` : `${day}${getDaySuffix(day)} of each month`;
    }
    if (type === 'custom') return language === 'da' ? `Hver ${value} dag(e)` : `Every ${value} day(s)`;
    return type;
  };

  if (!canView) {
    return (
      <div className="bossmenu-container">
        <div className="bossmenu-hero">
          <div className="bossmenu-header">
            <h1>{language === 'da' ? 'Virksomhedsøkonomi' : 'Company Finance'}</h1>
          </div>
        </div>
        <div className="bossmenu-layout">
          <div className="bossmenu-no-permission bossmenu-panel">
            <p>{language === 'da' ? 'Du har ikke adgang til virksomhedens økonomi.' : 'You do not have access to company finance.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bossmenu-container">
      <div className="bossmenu-hero">
        <div className="bossmenu-header">
          <p className="bossmenu-kicker">{language === 'da' ? 'Virksomhedskontrol' : 'Company control'}</p>
          <h1>{language === 'da' ? 'Virksomhedsøkonomi' : 'Company Finance'}</h1>
          <p>{language === 'da' ? 'Administrer virksomhedskonti og økonomi' : 'Manage company accounts and economy'}</p>
        </div>

        <div className="bossmenu-hero-stats">
          <div className="summary-card">
            <span className="summary-label">{language === 'da' ? 'Virksomheder' : 'Companies'}</span>
            <strong>{filteredCompanies.length}</strong>
            <small>{language === 'da' ? 'Tilgængelige konti' : 'Available accounts'}</small>
          </div>
          <div className="summary-card">
            <span className="summary-label">{language === 'da' ? 'Valgt konto' : 'Selected account'}</span>
            <strong>{selectedCompany ? selectedCompany.label : (language === 'da' ? 'Ingen' : 'None')}</strong>
            <small>{selectedCompany ? selectedCompany.name : (language === 'da' ? 'Vælg i listen' : 'Pick one from the list')}</small>
          </div>
          <div className="summary-card">
            <span className="summary-label">{language === 'da' ? 'Saldo' : 'Balance'}</span>
            <strong>{selectedCompany ? formatCurrency(selectedCompanyStats.balance) : '—'}</strong>
            <small>{selectedCompany ? `${selectedCompanyStats.accounts} ${language === 'da' ? 'poster' : 'entries'}` : (language === 'da' ? 'Ingen data endnu' : 'No data yet')}</small>
          </div>
        </div>
      </div>

      {error && <div className="bossmenu-error">{error}</div>}
      {success && <div className="bossmenu-success">{success}</div>}

      <div className="bossmenu-layout">
        <div className="bossmenu-sidebar bossmenu-panel">
          <div className="bossmenu-sidebar-header">
            <h2>{language === 'da' ? 'Virksomheder' : 'Companies'}</h2>
            <span className="bossmenu-count">{filteredCompanies.length}</span>
          </div>

          <div className="bossmenu-search-box">
            <input
              type="text"
              className="bossmenu-search-input"
              placeholder={language === 'da' ? 'Søg virksomhed...' : 'Search company...'}
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
            />
            {companySearch && (
              <button
                className="bossmenu-search-clear"
                onClick={() => setCompanySearch('')}
                title={language === 'da' ? 'Ryd søgning' : 'Clear search'}
              >
                ✕
              </button>
            )}
          </div>

          {loading && companies.length === 0 ? (
            <p className="bossmenu-loading">{language === 'da' ? 'Indlæser...' : 'Loading...'}</p>
          ) : filteredCompanies.length > 0 ? (
            <div className="bossmenu-company-list">
              {filteredCompanies.map((company) => (
                <button
                  key={company.name}
                  className={`bossmenu-company-item ${selectedCompany?.name === company.name ? 'active' : ''}`}
                  onClick={() => handleCompanySelect(company)}
                >
                  <span className="company-label">{company.label}</span>
                  <span className="company-shared">{company.shared ? (language === 'da' ? 'Delt' : 'Shared') : (language === 'da' ? 'Privat' : 'Private')}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="bossmenu-no-results">{language === 'da' ? 'Ingen virksomheder fundet' : 'No companies found'}</p>
          )}
        </div>

        <div className="bossmenu-main bossmenu-panel">
          {selectedCompany ? (
            <>
              <div className="bossmenu-company-header">
                <div>
                  <h2>{selectedCompany.label}</h2>
                  <p className="bossmenu-company-name">{selectedCompany.name}</p>
                </div>
                <div className="company-info">
                  <span className="info-badge">{selectedCompany.shared ? (language === 'da' ? 'Delt konto' : 'Shared Account') : (language === 'da' ? 'Privat konto' : 'Private Account')}</span>
                  <span className="info-badge total-balance">{formatCurrency(getTotalBalance())}</span>
                </div>
              </div>

              <div className="bossmenu-tabs">
                <button className={`tab-btn ${activeTab === 'instant' ? 'active' : ''}`} onClick={() => setActiveTab('instant')}>
                  {language === 'da' ? 'Øjeblikkelig' : 'Instant'}
                </button>
                <button className={`tab-btn ${activeTab === 'scheduled' ? 'active' : ''}`} onClick={() => setActiveTab('scheduled')}>
                  {language === 'da' ? 'Planlagt' : 'Scheduled'}
                </button>
              </div>

              {activeTab === 'instant' && (
                <>
                  <div className="bossmenu-actions">
                        {canEdit ? (
                          <>
                            <button
                              className="bossmenu-action-btn deposit-btn"
                              onClick={() => {
                                setShowDepositForm(!showDepositForm);
                                setShowWithdrawForm(false);
                                setError(null);
                              }}
                            >
                              {language === 'da' ? 'Indsæt' : 'Deposit'}
                            </button>
                            <button
                              className="bossmenu-action-btn withdraw-btn"
                              onClick={() => {
                                setShowWithdrawForm(!showWithdrawForm);
                                setShowDepositForm(false);
                                setError(null);
                              }}
                            >
                              {language === 'da' ? 'Hæv' : 'Withdraw'}
                            </button>
                          </>
                        ) : (
                          <div className="no-perm-actions">
                            <button className="bossmenu-action-btn" disabled>{language === 'da' ? 'Ingen tilladelse' : 'No permission'}</button>
                          </div>
                        )}
                  </div>

                  {showDepositForm && (
                    <form className="bossmenu-form deposit-form" onSubmit={handleDeposit}>
                      <h3>{language === 'da' ? 'Indsæt penge' : 'Deposit Money'}</h3>
                      <div className="form-group">
                        <label>{language === 'da' ? 'Beløb' : 'Amount'}</label>
                        <input type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="0" min="1" required />
                      </div>
                      <div className="form-actions">
                        <button type="submit" disabled={operationLoading} className="btn-confirm">
                          {operationLoading ? (language === 'da' ? 'Behandler...' : 'Processing...') : (language === 'da' ? 'Bekræft' : 'Confirm')}
                        </button>
                        <button type="button" onClick={() => { setShowDepositForm(false); setDepositAmount(''); }} className="btn-cancel">
                          {language === 'da' ? 'Annuller' : 'Cancel'}
                        </button>
                      </div>
                    </form>
                  )}

                  {showWithdrawForm && (
                    <form className="bossmenu-form withdraw-form" onSubmit={handleWithdraw}>
                      <h3>{language === 'da' ? 'Hæv penge' : 'Withdraw Money'}</h3>
                      <div className="form-group">
                        <label>{language === 'da' ? 'Beløb' : 'Amount'}</label>
                        <input type="number" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="0" min="1" required />
                      </div>
                      <div className="form-actions">
                        <button type="submit" disabled={operationLoading} className="btn-confirm">
                          {operationLoading ? (language === 'da' ? 'Behandler...' : 'Processing...') : (language === 'da' ? 'Bekræft' : 'Confirm')}
                        </button>
                        <button type="button" onClick={() => { setShowWithdrawForm(false); setWithdrawAmount(''); }} className="btn-cancel">
                          {language === 'da' ? 'Annuller' : 'Cancel'}
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="bossmenu-account-table bossmenu-card-shell">
                    <h3>{language === 'da' ? 'Kontosaldi' : 'Account Balances'} ({accountData.length})</h3>
                    {loading ? (
                      <p className="bossmenu-loading">{language === 'da' ? 'Indlæser...' : 'Loading...'}</p>
                    ) : accountData.length > 0 ? (
                      <div className="table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>{language === 'da' ? 'Beløb' : 'Amount'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {accountData.map((row, idx) => (
                              <tr key={idx}>
                                <td className="amount">{formatCurrency(row.money)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-data">{language === 'da' ? 'Ingen kontosaldi fundet' : 'No account balances found'}</p>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'scheduled' && (
                <>
                  <div className="bossmenu-actions">
                    {canSchedule ? (
                      <button
                        className="bossmenu-action-btn schedule-btn"
                        onClick={() => {
                          setShowScheduleForm(!showScheduleForm);
                          setError(null);
                        }}
                      >
                        {language === 'da' ? 'Planlæg indsætning' : 'Schedule Deposit'}
                      </button>
                    ) : (
                      <button className="bossmenu-action-btn" disabled>{language === 'da' ? 'Ingen tilladelse' : 'No permission'}</button>
                    )}
                  </div>

                  {showScheduleForm && (
                    <form className="bossmenu-form schedule-form" onSubmit={handleCreateSchedule}>
                      <h3>{language === 'da' ? 'Planlæg periodisk indsætning' : 'Schedule Periodic Deposit'}</h3>
                      <div className="form-group">
                        <label>{language === 'da' ? 'Beløb' : 'Amount'}</label>
                        <input type="number" value={scheduleFormData.amount} onChange={(e) => setScheduleFormData({ ...scheduleFormData, amount: e.target.value })} placeholder="0" min="1" required />
                      </div>
                      <div className="form-group">
                        <label>{language === 'da' ? 'Interval' : 'Interval'}</label>
                        <select value={scheduleFormData.interval_type} onChange={(e) => setScheduleFormData({ ...scheduleFormData, interval_type: e.target.value })}>
                          <option value="daily">{language === 'da' ? 'Daglig' : 'Daily'}</option>
                          <option value="weekly">{language === 'da' ? 'Ugentlig' : 'Weekly'}</option>
                          <option value="monthly">{language === 'da' ? 'Månedlig' : 'Monthly'}</option>
                          <option value="custom">{language === 'da' ? 'Brugerdefineret' : 'Custom'}</option>
                        </select>
                      </div>
                      {scheduleFormData.interval_type === 'custom' && (
                        <div className="form-group">
                          <label>{language === 'da' ? 'Dage mellem indsætninger' : 'Days between deposits'}</label>
                          <input type="number" value={scheduleFormData.interval_days} onChange={(e) => setScheduleFormData({ ...scheduleFormData, interval_days: e.target.value })} min="1" required />
                        </div>
                      )}
                      {scheduleFormData.interval_type === 'monthly' && (
                        <div className="form-group">
                          <label>{language === 'da' ? 'Dag i måneden' : 'Day of month'}</label>
                          <input
                            type="number"
                            value={scheduleFormData.monthly_day}
                            onChange={(e) => setScheduleFormData({ ...scheduleFormData, monthly_day: Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 31) })}
                            min="1"
                            max="31"
                            required
                          />
                          <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {language === 'da' ? 'F.eks. 1 for første dag, 15 for 15. dag' : 'E.g. 1 for 1st, 15 for 15th'}
                          </small>
                        </div>
                      )}
                      <div className="form-row">
                        <div className="form-group">
                          <label>{language === 'da' ? 'Time' : 'Hour'}</label>
                          <input type="number" value={scheduleFormData.execution_hour} onChange={(e) => setScheduleFormData({ ...scheduleFormData, execution_hour: e.target.value })} min="0" max="23" />
                        </div>
                        <div className="form-group">
                          <label>{language === 'da' ? 'Minut' : 'Minute'}</label>
                          <input type="number" value={scheduleFormData.execution_minute} onChange={(e) => setScheduleFormData({ ...scheduleFormData, execution_minute: e.target.value })} min="0" max="59" />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>{language === 'da' ? 'Noter' : 'Notes'}</label>
                        <textarea value={scheduleFormData.notes} onChange={(e) => setScheduleFormData({ ...scheduleFormData, notes: e.target.value })} placeholder={language === 'da' ? 'Valgfrit...' : 'Optional...'} rows="3" />
                      </div>
                      <div className="form-actions">
                        <button type="submit" disabled={operationLoading} className="btn-confirm">
                          {operationLoading ? (language === 'da' ? 'Opretter...' : 'Creating...') : (language === 'da' ? 'Planlæg' : 'Schedule')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowScheduleForm(false);
                            setScheduleFormData({
                              amount: '',
                              interval_type: 'daily',
                              interval_days: 1,
                              monthly_day: 1,
                              execution_hour: 0,
                              execution_minute: 0,
                              notes: ''
                            });
                          }}
                          className="btn-cancel"
                        >
                          {language === 'da' ? 'Annuller' : 'Cancel'}
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="bossmenu-account-table scheduled-deposits-table bossmenu-card-shell">
                    <h3>{language === 'da' ? 'Planlagte indsætninger' : 'Scheduled Deposits'} ({selectedSchedules.length})</h3>
                    {selectedSchedules.length > 0 ? (
                      <div className="table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>{language === 'da' ? 'Beløb' : 'Amount'}</th>
                              <th>{language === 'da' ? 'Interval' : 'Interval'}</th>
                              <th>{language === 'da' ? 'Næste udførelse' : 'Next Execution'}</th>
                              <th>{language === 'da' ? 'Status' : 'Status'}</th>
                              <th>{language === 'da' ? 'Handlinger' : 'Actions'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSchedules.map((schedule) => (
                              <tr key={schedule.id}>
                                <td className="amount">{formatCurrency(schedule.amount)}</td>
                                <td>{getIntervalLabel(schedule.interval_type, schedule.interval_days)}</td>
                                <td>{formatDate(schedule.next_execution)}</td>
                                <td>
                                  <span className={`status-badge ${schedule.is_active ? 'active' : 'inactive'}`}>
                                    {schedule.is_active ? (language === 'da' ? 'Aktiv' : 'Active') : (language === 'da' ? 'Inaktiv' : 'Inactive')}
                                  </span>
                                </td>
                                <td>
                                  {canSchedule ? (
                                    <button
                                      className="btn-delete-small"
                                      onClick={() => handleDeleteSchedule(schedule.id)}
                                      title={language === 'da' ? 'Slet' : 'Delete'}
                                    >
                                      🗑️
                                    </button>
                                  ) : (
                                    <button className="btn-delete-small" disabled title={language === 'da' ? 'Ingen tilladelse' : 'No permission'}>—</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-data">{language === 'da' ? 'Ingen planlagte indsætninger' : 'No scheduled deposits'}</p>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="bossmenu-placeholder">
              <p>{language === 'da' ? 'Vælg en virksomhed for at begynde' : 'Select a company to get started'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BossMenu;
